import React, { useMemo } from 'react'
import { Button } from '@surecompanies/core_components'
import { CloseOutlined, LockOutlined, UnlockOutlined } from '@ant-design/icons'
import { UserListRow } from '../../api/dto/user-invitation.dto'
import { countByDisplayStatus } from './userDisplay'
import {
  BulkBar,
  BulkBarActions,
  BulkBarBreakdown,
  BulkBarCount,
  BulkBarInfo,
  BulkBarStat,
  BulkBarStatValue,
} from './UserManagement.styles'
import type { AccessRemovalPlan } from './accessRemovalBuckets'
import { grantableRows } from './grantBuckets'
import type { GrantBuckets } from './grantBuckets'

interface BulkActionBarProps {
  selectedCount: number
  selectedRows: UserListRow[]
  grantBuckets: GrantBuckets
  removalPlan: AccessRemovalPlan
  onClear: () => void
  onGrantAccess: () => void
  onRemoveAccess: () => void
}

/**
 * Floating bar for the current selection, broken down by the status column.
 * What each action would do is counted per action and so overlaps; a status
 * tally is one row per person and adds up to the selection.
 */
const BulkActionBar = ({
  selectedCount,
  selectedRows,
  grantBuckets,
  removalPlan,
  onClear,
  onGrantAccess,
  onRemoveAccess,
}: BulkActionBarProps) => {
  const grantableCount = grantableRows(grantBuckets).length
  const removableCount = removalPlan.targets.length

  const stats = useMemo(
    () => countByDisplayStatus(selectedRows),
    [selectedRows]
  )

  return (
    <BulkBar>
      <BulkBarInfo>
        <BulkBarCount>
          {selectedCount} user{selectedCount === 1 ? '' : 's'} selected
        </BulkBarCount>
        {stats.length > 0 ? (
          <BulkBarBreakdown>
            {stats.map(({ label, count }) => (
              <BulkBarStat key={label}>
                <BulkBarStatValue>{count}</BulkBarStatValue> {label}
              </BulkBarStat>
            ))}
          </BulkBarBreakdown>
        ) : null}
      </BulkBarInfo>
      <BulkBarActions>
        <Button
          size="small"
          danger
          icon={<LockOutlined />}
          onClick={onRemoveAccess}
          disabled={removableCount === 0}
        >
          Remove Finance Manager
        </Button>
        <Button
          size="small"
          type="primary"
          icon={<UnlockOutlined />}
          onClick={onGrantAccess}
          disabled={grantableCount === 0}
        >
          Grant Finance Manager
        </Button>
        <Button
          size="small"
          type="text"
          icon={<CloseOutlined />}
          onClick={onClear}
          aria-label="Clear selection"
        />
      </BulkBarActions>
    </BulkBar>
  )
}

export { BulkActionBar }
